import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ApplyConfirmModal from './ApplyConfirmModal';
import { PREVIEW_LOCK_NOTE_ID } from '@/shared/hooks/usePreviewLock';
import useRevealAlert from '@/shared/hooks/useRevealAlert';
import StatusPill from '@/components/common/StatusPill';
import { positionKeyFacts } from '@/shared/utils/positionKeyFacts';
import {
    meetingLines,
    positionCourse,
    positionHeading,
} from '@/shared/utils/positionText';
import './OpenPositionsPage.css';

const NOT_OPEN_MESSAGE = 'This position is no longer open.';
const SEND_FAILED_MESSAGE = "Your application couldn't be sent. Try again.";

function errorMessageFor(error) {
    const status = error?.response?.status;
    if (status === 404) return NOT_OPEN_MESSAGE;
    if ([400, 409].includes(status) && error.response.data?.message) {
        return error.response.data.message;
    }
    return SEND_FAILED_MESSAGE;
}

function StageBadge({ stage }) {
    return <StatusPill family="candidacy" value={stage} />;
}

/**
 * One open position and the one thing the student can do about it, chosen by
 * `applyState` (see applyStateFor in OpenPositionsPage). `onApply` resolves
 * false when the page ignored the call because one for this position is
 * already in flight.
 */
export default function PositionCard({
    position,
    hoursPerWeek,
    applyState,
    onApply,
    locked,
    headingRef,
    confirm,
}) {
    const heading = positionHeading(position);
    const lines = meetingLines(position.meetings);
    const [pending, setPending] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [doneMessage, setDoneMessage] = useState('');
    const statusRef = useRef(null);
    const alertRef = useRevealAlert(errorMessage);

    useEffect(() => {
        if (doneMessage) statusRef.current?.focus();
    }, [doneMessage]);

    const [confirming, setConfirming] = useState(false);

    // The modal shows its own error and stays open; it closes on success.
    const send = async (resubmit, answers) => {
        setPending(true);
        setErrorMessage('');
        setDoneMessage('');
        let sent;
        try {
            sent = await onApply(position, { resubmit, answers });
        } catch (error) {
            setPending(false);
            throw error;
        }
        if (sent === false) return;
        setPending(false);
        setConfirming(false);
        setDoneMessage(
            resubmit
                ? `You resubmitted your application to ${heading}.`
                : `You applied to ${heading}.`
        );
    };

    const hiddenCourse = <span className="visually-hidden"> to {heading}</span>;
    const namedCourse = <span className="visually-hidden"> for {heading}</span>;

    const renderAction = () => {
        switch (applyState.kind) {
            case 'link':
                return (
                    <Button
                        as={Link}
                        to={applyState.to}
                        variant="primary"
                        size="sm"
                    >
                        {applyState.label || 'Apply to this position'}
                        {namedCourse}
                    </Button>
                );
            case 'apply':
            case 'resubmit': {
                const resubmit = applyState.kind === 'resubmit';
                return (
                    <>
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={pending || locked}
                            aria-describedby={
                                locked ? PREVIEW_LOCK_NOTE_ID : undefined
                            }
                            onClick={() => setConfirming(true)}
                        >
                            {resubmit ? 'Resubmit' : 'Apply to this position'}
                            {resubmit ? hiddenCourse : namedCourse}
                        </Button>
                        <ApplyConfirmModal
                            show={confirming}
                            position={position}
                            hoursPerWeek={hoursPerWeek}
                            askCourse={confirm?.askCourse === true}
                            requirementsFailed={
                                confirm?.requirementsFailed === true
                            }
                            onRetryRequirements={confirm?.retryRequirements}
                            accepted={confirm?.accepted ?? 0}
                            maxPositions={confirm?.maxPositions ?? null}
                            applicationLink={confirm?.applicationLink}
                            trainingText={confirm?.trainingText}
                            resubmit={resubmit}
                            errorText={errorMessageFor}
                            onConfirm={(answers) => send(resubmit, answers)}
                            onCancel={() => setConfirming(false)}
                        />
                    </>
                );
            }
            case 'applied': {
                const { candidacy, profileId } = applyState;
                return (
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <span className="fw-semibold">Applied</span>
                        {candidacy.stage !== 'applied' && (
                            <StageBadge stage={candidacy.stage} />
                        )}
                        {profileId && (
                            <Link to={`/my-applications/${profileId}`}>
                                View your application
                                <span className="visually-hidden">
                                    {' '}
                                    for {heading}
                                </span>
                            </Link>
                        )}
                    </div>
                );
            }
            case 'stage':
                return <StageBadge stage={applyState.candidacy.stage} />;
            default:
                return null;
        }
    };

    const [expanded, setExpanded] = useState(false);
    const detailId = `position-${position.applicationId}-detail`;
    const title = [positionCourse(position), position.title]
        .filter(Boolean)
        .join(': ');
    const action = renderAction();
    const trainingText = confirm?.trainingText;

    return (
        <Card className="h-100">
            <Card.Body className="d-flex flex-column">
                <div className="d-flex flex-column flex-md-row justify-content-between gap-2">
                    <h2
                        className="h5 mb-0"
                        id={`position-${position.applicationId}`}
                        tabIndex={-1}
                        ref={headingRef}
                    >
                        {title || heading}
                    </h2>
                </div>
                <p className="mb-1 mt-2 fw-semibold">
                    {positionKeyFacts(position, { hoursPerWeek })}
                </p>
                {position.instructorName && (
                    <p className="mb-2 text-body-secondary">
                        {position.instructorName}
                    </p>
                )}
                {trainingText && <p className="small mb-2">{trainingText}</p>}
                {position.recruitingMessage && (
                    <p
                        className={`text-break psh-recruiting-message mb-1 ${expanded ? '' : 'psh-clamp-2'}`}
                    >
                        {position.recruitingMessage}
                    </p>
                )}
                {action && (
                    <div className="psh-position-action my-2">{action}</div>
                )}
                <div>
                    <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        aria-expanded={expanded}
                        aria-controls={detailId}
                        onClick={() => setExpanded((open) => !open)}
                    >
                        {expanded ? 'Hide details' : 'Position details'}
                        <span className="visually-hidden"> for {heading}</span>
                    </Button>
                </div>
                <div id={detailId} hidden={!expanded}>
                    <dl className="row mb-0 mt-2 small">
                        {position.crn && (
                            <>
                                <dt className="col-sm-3">CRN</dt>
                                <dd className="col-sm-9">{position.crn}</dd>
                            </>
                        )}
                        {position.section && (
                            <>
                                <dt className="col-sm-3">Section</dt>
                                <dd className="col-sm-9">{position.section}</dd>
                            </>
                        )}
                        {lines.length > 0 && (
                            <>
                                <dt className="col-sm-3">Meets</dt>
                                <dd className="col-sm-9">
                                    {lines.map((line) => (
                                        <div key={line}>{line}</div>
                                    ))}
                                </dd>
                            </>
                        )}
                    </dl>
                </div>
                {errorMessage && (
                    <Alert
                        variant="warning"
                        tabIndex={-1}
                        ref={alertRef}
                        className="mb-2 mt-2"
                    >
                        {errorMessage}
                    </Alert>
                )}
                <div role="status" tabIndex={-1} ref={statusRef}>
                    {doneMessage}
                </div>
            </Card.Body>
        </Card>
    );
}
